import React, {useState, useEffect} from "react";
import {useSelector, useDispatch} from "react-redux";
import useOnClickOutside from "use-onclickoutside";

import * as actionTypes from "../../store/actions/actionTypes";
import {archiveAdmissionDocuments, resendSms, executeCopyPreAddData} from "../../store/actions/admission";
import {useToasts} from "react-toast-notifications";
import UrDt from "../CustomLogic/UrDt";
import AdmDt from "../CustomLogic/AdmDt";
import DownloadAdmissionDocumentPdf from "../UI/DownloadAdmissionDocumentPdf";

const COPY_OPTIONS = {
    IGNORE: 'ignore',
    COPY_DATA: 'copyData',
    AUTO_COMPLETE: 'autoComplete'
};

const ModalConfirmAdmissionAction = () => {
    const {show, admission, actionLabel, actionDispatchable, documentTypesToCopy, isCopyPreAddData} = useSelector(state => state.modal.confirmAdmissionAction);
    const {devSmsTarget_enable, devSmsTarget_phone} = useSelector(state => state.auth);
    const [documentOptions, setDocumentOptions] = useState({});

    const dispatch = useDispatch();
    const ref = React.useRef(null);
    useOnClickOutside(ref, () => hideModal());

    // Initialize per-document options when documentTypesToCopy changes.
    // Default action = autoComplete; default source = first available (most recent).
    useEffect(() => {
        if (documentTypesToCopy && documentTypesToCopy.length > 0) {
            const initialOptions = {};
            documentTypesToCopy.forEach(doc => {
                const firstSource = doc.sources && doc.sources.length > 0 ? doc.sources[0] : null;
                initialOptions[doc.id] = {
                    option: firstSource ? COPY_OPTIONS.AUTO_COMPLETE : COPY_OPTIONS.IGNORE,
                    sourceAdmissionId: firstSource ? firstSource.admissionId : null,
                };
            });
            setDocumentOptions(initialOptions);
        }
    }, [documentTypesToCopy]);

    const hideModal = () => {
        dispatch({type: actionTypes.MODAL_CONFIRM_ADMISSION_ACTION_HIDE});
        setDocumentOptions({});
    };

    const handleOptionChange = (docId, value) => {
        setDocumentOptions(prev => ({
            ...prev,
            [docId]: {...(prev[docId] || {}), option: value}
        }));
    };

    const handleSourceChange = (docId, sourceAdmissionId) => {
        setDocumentOptions(prev => ({
            ...prev,
            [docId]: {...(prev[docId] || {}), sourceAdmissionId: parseInt(sourceAdmissionId, 10)}
        }));
    };

    const doAction = () => {
        dispatch({type: actionTypes.RELOADING_PROGRESS_START, id: admission.id});
        if (isCopyPreAddData) {
            dispatch(executeCopyPreAddData(admission, documentOptions));
        } else if (actionDispatchable) {
            dispatch(actionDispatchable);
        }
        hideModal();
    }

    const showTargetPhone = () => {
        if (devSmsTarget_enable) {
            return (
                <dd><strike>{admission.mobilePhone}</strike> <span className="badge badge-danger">DEV TARGET ENABLED = {devSmsTarget_phone}</span></dd>
            );
        } else {
            return (
                <dd>{admission.mobilePhone}</dd>
            );
        }
    }

    const renderDocumentRow = (doc) => {
        const currentOption = documentOptions[doc.id] || {};
        const sources = doc.sources || [];
        const hasSources = sources.length > 0;
        const isIgnored = currentOption.option === COPY_OPTIONS.IGNORE;
        const selectedSource = sources.find(s => s.admissionId === currentOption.sourceAdmissionId);

        return (
            <tr key={doc.id}>
                <td>{doc.title}</td>
                <td>
                    <select
                        className="form-control"
                        value={currentOption.option || COPY_OPTIONS.AUTO_COMPLETE}
                        onChange={(e) => handleOptionChange(doc.id, e.target.value)}
                        disabled={!hasSources}
                    >
                        <option value={COPY_OPTIONS.IGNORE}>Ignore</option>
                        <option value={COPY_OPTIONS.COPY_DATA}>Copy patient data</option>
                        <option value={COPY_OPTIONS.AUTO_COMPLETE}>Copy patient data and Auto-complete</option>
                    </select>
                </td>
                <td>
                    {hasSources ? (
                        <select
                            className="form-control"
                            value={currentOption.sourceAdmissionId || ''}
                            onChange={(e) => handleSourceChange(doc.id, e.target.value)}
                            disabled={isIgnored}
                        >
                            {sources.map((src) => (
                                <option key={src.admissionId} value={src.admissionId}>{src.label}</option>
                            ))}
                        </select>
                    ) : (
                        <span className="text-muted">No source available</span>
                    )}
                </td>
                <td>
                    {selectedSource && !isIgnored && (
                        <DownloadAdmissionDocumentPdf
                            doc={{id: selectedSource.admissionDocumentId, title: doc.title}}
                        />
                    )}
                </td>
            </tr>
        );
    };

    const renderModal = () => {
        return (
            <div className="modal-dialog modal-lg" role="document" ref={ref}>
            <div className="modal-content modal-content-fit">
                <button type="button" className="close" data-dismiss="modal" aria-label="Close" onClick={hideModal}>
                    <span aria-hidden="true">×</span>
                </button>
                <div className="modal-body">
                    <h3>{actionLabel} for {admission.title} {admission.surname}, {admission.name}</h3>
                    <dl className="person-list mb-3">
                        <UrDt admission={admission}/>
                        <AdmDt admission={admission}/>
                        <dt>Mobile</dt>
                        {showTargetPhone()}
                    </dl>
                    <form action="#" className="person-info-form">
                        <div className="form-group mb-4">
                            This change isn't reversible
                        </div>
                        {documentTypesToCopy && documentTypesToCopy.length > 0 && (
                            <table className="table" cellSpacing="0" width="100%">
                                <thead>
                                <tr>
                                    <th><strong className="title">Document</strong></th>
                                    <th><strong className="title">Action</strong></th>
                                    <th><strong className="title">Source</strong></th>
                                    <th><strong className="title">Preview</strong></th>
                                </tr>
                                </thead>
                                <tbody>
                                {documentTypesToCopy.map(renderDocumentRow)}
                                </tbody>
                            </table>
                        )}
                        <div className="form-group">
                            <div className="row">
                                <div className="col-12 d-flex justify-content-end">
                                    <input type="button" value={actionLabel} className="btn btn-primary" onClick={doAction}/>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
        );
    }

    return (
        <>
            <div className={`modal fade ${show ? 'show' : ''}`} id="personModal" tabIndex="-1" role="dialog"
                 aria-labelledby="personModalLabel" aria-modal="true">
                {show && renderModal()}
            </div>
        </>
    );
}

export default ModalConfirmAdmissionAction;
